import { useEffect, useRef } from "react";
import { MessageBubble } from "./MessageBubble";
import type { ChatMessage } from "../types/chat";

interface ChatThreadProps {
  messages: ReadonlyArray<ChatMessage>;
  errorMessage?: string | null;
  onRetry?: () => void;
  onConfirm?: () => void;
  onReject?: () => void;
}

/**
 * Scrollable message thread. Auto-scrolls to the bottom on new messages.
 */
export function ChatThread({ messages, errorMessage, onRetry, onConfirm, onReject }: ChatThreadProps) {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        {errorMessage && (
          <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm font-medium flex items-center justify-between gap-3">
            <span>{errorMessage}</span>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="px-3 h-7 rounded-full bg-destructive/20 text-destructive text-xs font-bold hover:bg-destructive/30 transition-colors"
              >
                Retry
              </button>
            )}
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            onConfirm={m.confirmation ? onConfirm : undefined}
            onReject={m.confirmation ? onReject : undefined}
          />
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
